import { callDelete, callGet, callPost, callPostWithFormData, CancelToken, delay, } from "./index";
import { UserDocument } from "../models/UserDocument";
import { UserDocumentVersion } from "../models/UserDocumentVersion";
import { AxiosResponse, CancelTokenSource } from "axios";

type UploadProgressEvent = {
  loaded: number;
  total?: number;
  progress?: number;
  bytes: number;
  rate?: number;
  estimated?: number;
  upload?: boolean;
  download?: boolean;
  event?: any;
  lengthComputable: boolean;
};

export class DocumentsAPI {
  async getDocuments(
    estateId:number,
    userId:number,
    rethrowError = false
  ): Promise<UserDocument[] | undefined> {
    try {
      const response = await callGet(`estates/${estateId}/users/${userId}/documents`, rethrowError);
      return response?.data?.map((json: any) => UserDocument.fromJSON(json));
    } catch (error) {
      console.error(error, "getDocuments error");
    }
  }

  async getDocument(
    estateId:number,
    userId:number,
    id: number,
    rethrowError = false
  ): Promise<any> {
    try {
      const response = await callGet(`estates/${estateId}/users/${userId}/documents/${id}`, rethrowError);
      return response?.data;
    } catch (error) {
      console.error(error, "getDocument error");
    }
  }

  async getDocumentVersions(
    estateId:number,
    userId:number,
    document: UserDocument,
    rethrowError = false
  ): Promise<UserDocumentVersion[] | undefined> {
    try {
      const response = await callGet(`estates/${estateId}/users/${userId}/documents/${document.id}/versions`, rethrowError);
      return response?.data?.map((json: any) =>
        UserDocumentVersion.fromJSON(json)
      );
    } catch (error) {
      console.error(error, "getDocumentVersions error");
    }
  }

  async revertToDocumentVersion(
    estateId:number,
    userId:number,
    document: UserDocument,
    versionId: number,
    rethrowError = false
  ): Promise<UserDocumentVersion[] | undefined> {
    try {
      const response = await callPost(
        `estates/${estateId}/users/${userId}/documents/${document.id}/versions`,
        {
          version: versionId,
        },
        rethrowError
      );
      return response?.data?.map((json: any) =>
        UserDocumentVersion.fromJSON(json)
      );
    } catch (error) {
      console.error(error, "getDocumentVersions error");
    }
  }

  async fetchUrl(
    estateId:number,
    userId:number,
    id: number,
    rethrowError = false
  ): Promise<string | undefined> {
    try {
      const response = await callGet(`estates/${estateId}/users/${userId}/documents/${id}/url`, rethrowError);
      return response?.data?.url;
    } catch (error) {
      console.error(error, "fetchUrl error");
    }
  }

  async fetchUploadUrl(
    estateId:number,
    userId:number,
    id: number,
    fileName: string,
    rethrowError = false
  ): Promise<[string, string] | undefined> {
    try {
      console.log(fileName)
      let fileExtension = ""
      const filenameParts = fileName.split(".")
      if (filenameParts.length > 0) {
        fileExtension = filenameParts[filenameParts.length - 1]
      }

      let path = `estates/${estateId}/users/${userId}/documents/${id}/upload-url`;
      if (fileExtension) {
        path += "?file_ext=" + encodeURIComponent(fileExtension)
      }
      const response = await callGet(path, rethrowError);
      if (!response?.data) {
        return undefined
      }

      return [response.data.url, response.data.s3_key];
    } catch (error) {
      console.error(error, "fetchUploadUrl error");
    }
  }

  async postDocumentToS3(doc: File, uploadUrl: string, progressCallBack?: (percent: number) => void): Promise<boolean> {
    try {
      const xhr = new XMLHttpRequest();
      const success = await new Promise((resolve: (b: boolean) => any) => {
        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            console.log("upload progress:", event.loaded / event.total);
            if (progressCallBack) {
              progressCallBack(event.loaded / event.total)
            }
          }
        });
        xhr.addEventListener("loadend", () => {
          resolve(xhr.readyState === 4 && xhr.status === 200);
        });
        xhr.open("PUT", uploadUrl, true);
        xhr.setRequestHeader("Content-Type", "application/octet-stream");
        xhr.send(doc);
      });

      return success
    } catch (error) {
      throw new Error(`failed to create a document ${error}`)
    }
  }

  async saveDocument(
    estateId: number,
    userId: number,
    title?: string,
    documentId?: number,
    documentTypeId?: number,
    file?: File,
    cloudUrl?: string,
    notes?: string,
    folderId?: number,
    progressCallBack?: (percent: number) => void,
    artificialDelay = false,
    s3Key = "",
  ): Promise<UserDocument | null> {
    const isNew = !documentId;
    if (!title) {
      throw new Error("title is required");
    }
    if (!documentTypeId) {
      throw new Error("documentTypeId is required");
    }

    const formData = new FormData();
    formData.append("title", title);
    formData.append("document_type_id", documentTypeId.toString());
    if (cloudUrl) {
      formData.append("cloud_url", cloudUrl)
    }
    if (s3Key) {
      formData.append("s3_key", s3Key)
    }
    if (notes) {
      formData.append("notes", notes);
    }
    if (file) {
      formData.append("user_document", file, file.name);
    }
    if (folderId) {
      formData.append("folder_id", folderId.toString());
    }
    const onUploadProgress = (progressEvent?: UploadProgressEvent) => {
      const progress = progressEvent ? Math.round(
        (progressEvent.loaded * 100) / (progressEvent.total || 100)
      ) : 0;
      progressCallBack?.(progress);
    };
    try {
      let response;
      if (artificialDelay) {
        await delay(200);
        progressCallBack?.(10);
        await delay(200);
        progressCallBack?.(20);
        await delay(200);
        progressCallBack?.(30);
        await delay(200);
        progressCallBack?.(40);
      }

      if (isNew) {
        // create
        response = await callPostWithFormData(`estates/${estateId}/users/${userId}/documents`, formData, true, {
          onUploadProgress,
        });
      } else {
        // update
        formData.append("_method", "PUT");
        // ☝️laravel work-around for php bug
        //  https://github.com/laravel/framework/issues/13457#issuecomment-313949989
        response = await callPostWithFormData(
          `estates/${estateId}/users/${userId}/documents/${documentId}`,
          formData,
          true,
          {
            onUploadProgress,
          }
        );
      }
      documentId = documentId || response?.data?.message?.document_id

      return UserDocument.fromJSON({
        id: documentId || -1,
        version: {
          ...response?.data?.message,
          title: response?.data?.message?.title,
        },
        document_type_id: documentTypeId,
        notes,
        cloud_url: cloudUrl,
        created_at: new Date().toISOString(),
      });
    } catch (error) {
      console.log("Couldn't save document " + (error as any)?.message)
      console.error(error, "saveDocument error");
      return null
    }
  }

  async deleteDocument(
    estateId: number,
    userId: number,
    documentId: number,
    rethrowError = false
  ): Promise<boolean> {
    try {
      await callDelete(`estates/${estateId}/users/${userId}/documents/${documentId}`, rethrowError);
      return true;
    } catch (error) {
      if (rethrowError) {
        throw error
      }
      console.error(error, "deleteDocument error");
    }

    return false
  }

  
  static async getDocumentContents (
    estateId: number,
    userId: number,
    id: number,
    version?: number,
    thumbnail = false,
    cancelTokenSource?: CancelTokenSource,
  ): Promise<AxiosResponse | undefined> {
    let url = `estates/${estateId}/users/${userId}/documents/${id.toString()}`;
    if (version) {
      url += `/versions/${version}`;
    }
    if (thumbnail) {
      url += `/thumbnail`;
    }
    const response = await callGet(url, false, {
      responseType: "blob",
      cancelToken: cancelTokenSource?.token,
    });

    return response || undefined;
  }
}

